/* eslint-disable @typescript-eslint/no-floating-promises */
import { DmpAdapterName } from "parog-web/cnam-adapters/cnam-manager-factory";
import type {
  DetectCardResult,
  DirectAuthenticationStatus,
  DmpConnectDocument,
  DmpConnectFilterCriteria,
  DmpConnectJSDmpDocumentContent,
  DmpConnectJSInsIdentityResult,
  FindDocumentsReponsePayload,
  InsiConnectorResult,
  PracticeLocation,
  ReadCpxCardResult,
  ReadVitaleCardResult,
  WebPSURLs
} from "parog-web/cnam/types";
import { DmpAuthorizationStatus, InsOid } from "parog-web/cnam/types";
import type DmpHealthCentreConfig from "parog-web/models/dmp-health-centre-config";
import type {
  default as Medic,
  default as MedicModel
} from "parog-web/models/medic";
import type SemeiaFeaturesService from "parog-web/services/semeia-features";
import { SemeiaFeature } from "parog-web/services/semeia-features";
import {
  CnamApiCpsCardBlocked,
  CnamApiCpsCardError,
  CnamApiCpsCardWrongCode,
  CnamApiDmpAuthorizationDenied,
  CnamApiDmpStatusClosed,
  CnamApiDmpStatusNotFound,
  CnamApiError,
  CnamApiInsError,
  CnamApiVitaleCardError
} from "parog-web/utils/cnam-api-errors";
import { isDevelopmentOrLocal } from "parog-web/utils/runtime-environment";
import {
  getItemFromLocalStorage,
  removeItemFromLocalStorage,
  setItemInLocalStorage
} from "parog-web/utils/use-local-storage";
import DMPConnectHttpInstance from "./dmp-connect-http-instance";
import type { default as PinCodeManager } from "./pin-code-manager";

export type DmpConnectConfiguration = string;

interface DmpNetworkParams {
  s_proxyIpOrUrl?: string;
  i_proxyPort?: number;
  s_proxyLogin?: string;
  s_proxyPass?: string;
  s_ntpIpOrURl?: string;
  i_ntpPort?: number;
}

export interface NetworkParams {
  s_proxyServer?: string;
  i_proxyPort?: number;
  s_proxyLogin?: string;
  s_proxyPass?: string;

  s_ntpServer?: string;
  i_ntpPort?: number;

  s_tseCredentials?: string;
}

/**
 * The minimum interface to DmpConnect-JS.
 *
 * This API is on a local server, accessible through a WebSocket on
 * https://localhost.icanopee.net:9980 (v1) or 9982 (v2).
 *
 * The DMPConnectJS API is asynchronous: a command is launched, then at some
 * point a result is received through the socket.
 * The DMPConnectJS is not based on Promises, this Service transforms some calls
 * as Promises.
 *
 * Code is greatly inspired from the examples.js of the DMPConnectJS SDK.
 */

export default class DmpConnect {
  static get isDevelopmentMode(): boolean {
    return isDevelopmentOrLocal();
  }

  cachedNetworkParams: NetworkParams | undefined;
  declare instance: DMPConnect | DMPConnectHttpInstance;

  get connectionParams(): { tlsiServerFullUrl: string; dmpServerName: string } {
    if (this.isDevelopmentMode) {
      // Homologué server
      return {
        tlsiServerFullUrl: "https://qualiflps.services-ps.ameli.fr",
        dmpServerName: "lps.formation1"
      };

      // Development server
      // return {
      //   tlsiServerFullUrl: "https://qualiflps.services-ps.ameli.fr",
      //   dmpServerName: "formation1"
      // };
    }
    return {
      tlsiServerFullUrl: "https://services-ps.ameli.fr/lps",
      dmpServerName: ""
    };
  }

  get isDevelopmentMode(): boolean {
    const key = SemeiaFeature.DmpConnectIsDevelopmentMode;
    return (
      DmpConnect.isDevelopmentMode ||
      Boolean(this.semeiaFeatures?.isEnabled(key))
    );
  }

  get isRemoteMode(): boolean {
    return this.instance instanceof DMPConnectHttpInstance;
  }

  /**
   * Base64 string containing a cached version of the DmpConnect configuration.
   * The configuration is stored on the server as a static file and downloaded.
   */
  private declare _cachedDcParameters: DmpConnectConfiguration;
  private transactionsTimeoutInSeconds = 60;

  constructor(
    public pinCodeManager: PinCodeManager,
    public medic: MedicModel,
    public accessToken: string,
    public semeiaFeatures?: SemeiaFeaturesService
  ) {
    this.instance =
      medic.dmpAdapterName == DmpAdapterName.DMPConnectJsOlaqin
        ? new DMPConnectHttpInstance(accessToken)
        : new DMPConnect(
            (/*message*/) => {
              // This callback can help debug DMPConnectJS.
              // It displays a log of log coming from the client.
              // console.log(message);
            },
            () => {
              // Called when internal state has been updated.
            },
            (error, _command, _timeoutInSeconds, answerCallback) => {
              // Called when an error happens
              // console.error(
              //  `Error in dmpconnect.js when running ${command.s_commandName}`,
              //  error
              // );
              answerCallback({ error });
            },
            null,
            null,
            "localhost.icanopee.net"
          );
  }

  _createInsiConnector(): Promise<InsiConnectorResult> {
    return new Promise(async (resolve, reject) => {
      const transactionsTimeoutInSeconds = 2;

      this.instance.hl_createInsiConnector(
        this.pinCodeManager.pinCode as string,
        this.connectionParams.tlsiServerFullUrl as never,
        transactionsTimeoutInSeconds as never,
        // FIXME these parameters should be given to the method.
        // To be updated before pushing in production.
        "AMBULATOIRE" as never,
        0 as never,
        {},
        res => {
          if (res.error) {
            reject(
              new CnamApiError(
                `Cannot create an INSi connector (hl_createInsiConnector) : ${res.error.s_apiErrorDescription}`,
                res.error
              )
            );
          } else {
            resolve(res);
          }
        }
      );
    });
  }

  _createRemoteInsiConnector(): Promise<InsiConnectorResult> {
    return new Promise(async (resolve, reject) => {
      const transactionsTimeoutInSeconds = 2;

      (this.instance as DMPConnectHttpInstance).hl_createInsiConnector(
        this.connectionParams.tlsiServerFullUrl,
        transactionsTimeoutInSeconds,
        // FIXME these parameters should be given to the method.
        // To be updated before pushing in production.
        "AMBULATOIRE",
        0,
        {},
        res => {
          if (res.error) {
            reject(
              new CnamApiError(
                `Cannot create an INSi connector (hl_createInsiConnector) : ${res.error.s_apiErrorDescription}`,
                res.error
              )
            );
          } else {
            resolve(res);
          }
        }
      );
    });
  }

  async _readCPSCard(): Promise<ReadCpxCardResult> {
    const pinCode =
      this.pinCodeManager.pinCode || this.pinCodeManager.typedPinCode;

    return new Promise(async (resolve, reject) => {
      try {
        const cards = await this.detectCards();

        if (!cards.cps) {
          reject(new CnamApiCpsCardError(`CPS card is not detected`));
          return;
        }

        this.instance.hl_getCpxCard(cards.cps.index, cards.cps.name, result => {
          if (result.error) {
            reject(
              new CnamApiCpsCardError(
                `Cannot get CPS card content : ${result.error.s_apiErrorDescription}`,
                result.error
              )
            );
            return;
          }

          this.instance.hl_readCpxCard(
            pinCode as string & ((result: ReadCpxCardResult) => void),
            result => {
              if (result.error) {
                if (result.error.i_apiErrorCode === 8) {
                  reject(
                    new CnamApiCpsCardBlocked(
                      `Cannot read CPS card content (CPS card is blocked)`,
                      result.error
                    )
                  );
                } else if (result.error.i_apiErrorCode === 7) {
                  reject(
                    new CnamApiCpsCardWrongCode(
                      `Cannot read CPS card content (Wrong pin code)`,
                      result.error
                    )
                  );
                } else {
                  reject(
                    new CnamApiCpsCardError(
                      `Cannot get CPS card content : ${result.error.s_apiErrorDescription}`,
                      result.error
                    )
                  );
                }
              } else {
                this.pinCodeManager.cachePinCode(pinCode as string);
                this.pinCodeManager.typedPinCode = "";
                resolve(result);
              }
            }
          );
        });
      } catch (e) {
        reject(e);
      }
    });
  }

  async _readRemoteCPSCard(): Promise<ReadCpxCardResult> {
    return new Promise(async (resolve, reject) => {
      await this.instance.hl_getCpxCard(0, "WIRELESS CPX", result => {
        if (result.error) {
          reject(
            new CnamApiCpsCardError(
              `Cannot get CPS card content : ${result.error.s_apiErrorDescription}`,
              result.error
            )
          );
          return;
        }
      });

      return (this.instance as DMPConnectHttpInstance).hl_readCpxCard(
        result => {
          if (result.error) {
            if (result.error.i_apiErrorCode === 8) {
              reject(
                new CnamApiCpsCardBlocked(
                  `Cannot read CPS card content (CPS card is blocked)`,
                  result.error
                )
              );
            } else if (result.error.i_apiErrorCode === 7) {
              reject(
                new CnamApiCpsCardWrongCode(
                  `Cannot read CPS card content (Wrong pin code)`,
                  result.error
                )
              );
            } else {
              reject(
                new CnamApiError(
                  `Cannot get CPS card content : ${result.error.s_apiErrorDescription} - ${result.error.s_apiErrorExtendedInformations}`,
                  result.error
                )
              );
            }
          } else {
            resolve(result);
          }
        }
      );
    });
  }

  closeSession(): Promise<void> {
    return new Promise(async resolve => {
      // If the session is not opened, do not close it
      if (!(await this.sessionOpened())) {
        resolve();
      }

      this.instance.hl_closeSession(function (result) {
        if (result.error) {
          console.error(
            `Cannot close session (hl_closeSession) : ${result.error.s_apiErrorDescription}`
          );
          resolve();
        } else {
          resolve(result);
        }
      });
    });
  }

  async createConnector(
    cpsPracticeLocationIndice: number,
    practiceLocations: PracticeLocation[]
  ): Promise<void> {
    this.pinCodeManager.refreshCacheDate();
    let func: (
      practiceSetting: string,
      cpsPracticeLocationIndice: number,
      practiceLocations: PracticeLocation[]
    ) => Promise<unknown>;
    if (this.isRemoteMode) {
      func = this._createRemoteDMPConnector.bind(this);
    } else {
      func = this._createDMPConnector.bind(this);
    }

    await func(
      // The list of possible value are grouped under the guid 1.2.250.1.213.1.1.4.9
      // AMBULATOIRE is the one that is the most current use case for the usage of our app.
      // We don't need to ask it to the Medic.
      "AMBULATOIRE",
      cpsPracticeLocationIndice,
      practiceLocations
    );
  }

  async createInsiConnector(): Promise<InsiConnectorResult> {
    if (this.isRemoteMode) {
      return this._createRemoteInsiConnector();
    }

    return this._createInsiConnector();
  }

  /**
   *  This method retrieves connected Smart Cards and create a containing with
   *  the connected cards.
   *
   *  The hash categorizes the cards as CPS or Vitale cards.
   *
   *  TODO: observe the cards and update this _cards list through
   *  - hl_startVitaleCardMonitoring
   *  - stopVitaleCardMonitoring
   */
  detectCards(): Promise<DetectCardResult> {
    return new Promise((resolve, reject) => {
      this.instance.hl_getPcscReaders(function (result) {
        if (result.error) {
          reject(
            new CnamApiError(
              `Cannot get Pcs Readers : ${result.error.s_apiErrorDescription}`,
              result.error
            )
          );
          return;
        }

        // The code currently considers there can be only one CPS card and one Vitale card.
        const cards: DetectCardResult = {};
        for (let i = 0; i < result.Readers.length; i++) {
          const reader = result.Readers[i];

          switch (reader.i_slotType) {
            case 2:
              cards.vitale = {
                index: i,
                name: reader.s_name
              };
              break;
            case 3:
              cards.cps = {
                index: i,
                name: reader.s_name
              };
              break;
          }
        }

        resolve(cards);
      });
    });
  }

  findDocuments(
    nir: string,
    dmpConnectJSCriteria: DmpConnectFilterCriteria
  ): Promise<Array<DmpConnectDocument>> {
    return new Promise(async (resolve, reject) => {
      this.instance.hl_findDocuments(
        nir,
        dmpConnectJSCriteria,
        function (result: FindDocumentsReponsePayload) {
          if (result.error) {
            reject(
              new CnamApiError(
                `Cannot find documents (hl_findDocuments) `,
                result.error
              )
            );
            return;
          }

          resolve(result.Documents);
        }
      );
    });
  }

  getDirectAuthenticationDMPStatus(
    nir: string
  ): Promise<DirectAuthenticationStatus> {
    return new Promise(async (resolve, reject) => {
      this.instance.hl_getDirectAuthenticationDMPStatus(nir, function (result) {
        if (result.error) {
          reject(
            new CnamApiError(
              `Cannot get authentication (hl_getDirectAuthenticationDMPStatus) `,
              result.error
            )
          );
          return;
        }

        // First step, check the DMP status (whether it exists and is opened or not)
        const dmpStatus = result.ExistingTestAnswer.i_dmpStatus;
        switch (dmpStatus) {
          case DMPConnect.DMPStatus.DMPIsClosed:
            reject(
              new CnamApiDmpStatusClosed(
                `The DMP has been closed and must be re-opened (hl_getDirectAuthenticationDMPStatus)`,
                result.error
              )
            );
            break;
          case DMPConnect.DMPStatus.DMPNotFound:
            reject(
              new CnamApiDmpStatusNotFound(
                `No DMP found for this patient (hl_getDirectAuthenticationDMPStatus)`,
                result.error
              )
            );
            break;
          case DMPConnect.DMPStatus.DMPError:
            reject(
              new CnamApiError(
                `DMP Error occured (hl_getDirectAuthenticationDMPStatus)`,
                result.error
              )
            );
            break;
          case DMPConnect.DMPStatus.DMPExist:
            // The patient has a DMP opened and working.
            break;
        }

        // Second step: check that the medic has an authorization to consult it.
        const userAuthorizationStatus =
          result.ExistingTestAnswer.i_userAuthorization;
        switch (userAuthorizationStatus) {
          case DMPConnect.UserAuthorizationStatus.AuthorizationExist:
            resolve({
              authorizationStatus: DmpAuthorizationStatus.Authorized
            });
            break;
          case DMPConnect.UserAuthorizationStatus.AuthorizationExpired:
            resolve({
              authorizationStatus: DmpAuthorizationStatus.Expired
            });
            break;
          case DMPConnect.UserAuthorizationStatus.NoAuthorization:
            resolve({
              authorizationStatus: DmpAuthorizationStatus.NotAuthorized
            });
            break;
          case DMPConnect.UserAuthorizationStatus.AuthorizationDenied:
            reject(
              new CnamApiDmpAuthorizationDenied(
                `The medic is blacklisted for this DMP (hl_getDirectAuthenticationDMPStatus)`,
                result.error
              )
            );
            break;
          case DMPConnect.UserAuthorizationStatus.AuthorizationError:
            // Means that the DMP does not exists or was closed.
            // But we should never get there since there is a previous check on the DMP Status
            reject(
              new CnamApiError(
                `Fatal error - this should never happens (hl_getDirectAuthenticationDMPStatus) `,
                result.error
              )
            );
            break;
        }
      });
    });
  }

  getDocumentContentByUniqueId(
    nir: string,
    uniqueId: string,
    uuid: string,
    parameters: { i_getCdaHeaders: boolean; i_getCdaContent: boolean }
  ): Promise<DmpConnectJSDmpDocumentContent> {
    return new Promise(async (resolve, reject) => {
      this.instance.hl_getDocumentContentByUniqueId(
        nir,
        uniqueId,
        uuid,
        parameters,
        result => {
          if (result.error) {
            reject(
              new CnamApiError(
                `Cannot find documents (hl_getDocumentContent) `,
                result.error
              )
            );
            return;
          }
          resolve(result as DmpConnectJSDmpDocumentContent);
        }
      );
    });
  }

  /**
   * Input requirements:
   *
   * The input strings of this transaction must:
   * - only contains capitals letters ;
   * - not contain symbols (no accent, no cedilla) ;
   * - only contains hyphen ('-') and space (' ') as special characters, and hyphen and space cannot be doubled.
   *
   * The sex can not be "unknown".
   *
   * The expected date format is YYYY-MM-DD.
   *
   * The birthplace format is COG (Code Officiel Géographique, @see https://www.insee.fr/fr/information/2560452 )
   */
  getInsFromIdentityInformation(
    billingNumber: string,
    name: string,
    given: string,
    sex: number,
    date: string,
    place?: string
  ): Promise<DmpConnectJSInsIdentityResult> {
    const idamDevelopmentIcanopee = "NSC17HE20GM0";
    const idamProductionSemeia = "PLT17NF22FM1";
    // const lpsInfos = {
    //   s_idam: "FFC00NF12HW0", // Software authorization number. From the CNDA. (example value from examples_tlsi.html)
    //   s_numAM: "04", // IDAM type. Currently must be set to "04" (Authorization number).
    //   s_version: "1", // Software version. No specific format. E.g. "01.00", or "1.0", or "2".
    //   s_instance: "550e8400-e29b-41d4-a716-446655440000", //"550e8400-e29b-41d4-a716-446655440000", // Software instance UUID in its canonical form.
    //   s_name: "Semeia - Medics Web", //Semeia - Medics Web", // The software name.
    //   s_billingNumber: "" // Billing situation. Either FINESS for institutions or AM number in all other cases. Mandatory for INSi.
    // };

    const lpsInfoICanopee = {
      s_idam: this.isDevelopmentMode
        ? idamDevelopmentIcanopee
        : idamProductionSemeia, // Software authorization number. From the CNDA. (example value from examples_tlsi.html)
      s_numAM: "4", // IDAM type. Currently must be set to "04" (Authorization number).
      s_version: "1", // Software version. No specific format. E.g. "01.00", or "1.0", or "2".
      s_instance: "550e8400-e29b-41d4-a716-446655440000", //"550e8400-e29b-41d4-a716-446655440000", // Software instance UUID in its canonical form.
      s_name: "Pilot", //Semeia - Medics Web", // The software name.
      s_billingNumber: billingNumber // Billing situation. Either FINESS for institutions or AM number in all other cases. Mandatory for INSi.
    };

    return new Promise(async (resolve, reject) => {
      this.instance.hl_getInsFromIdentityInformation(
        lpsInfoICanopee,
        name,
        given,
        sex,
        date,
        place,
        res => {
          if (this.isDevelopmentMode && res.Identity) {
            // The INS service returns a "production" OID, while a test or demo one is expected for calling the DMP afterwards.
            // Forcing to test or demo so that the full INS / DMP cycle can be tested.
            if (res.Identity.Ins.s_value == "2550699999999") {
              // This is Nathalie Desmaux, which is a demo patient
              // However, Nathalie Desmaux does not currently exist in the devh environment.
              res.Identity.Ins.s_oid = InsOid.DEMO;
            } else {
              res.Identity.Ins.s_oid = InsOid.TEST;
            }
          }

          if (res.error) {
            console.log(res.error);
            reject(
              new CnamApiInsError(
                `Cannot get the patient INS from identity information (hl_getInsFromIdentityInformation): ${res.error.s_apiErrorTlsiErrorDetailError}`,
                res.error
              )
            );
          }

          resolve(res);
        }
      );
    });
  }

  getWebPsDmpUrls(nir: string): Promise<WebPSURLs> {
    return new Promise((resolve, reject) => {
      return this.instance.hl_getWebPsDmpUrls(nir, 0, result => {
        if (result.error) {
          reject(new CnamApiError("Cannot get Web PS URLs", result.error));
        } else {
          resolve({
            medicDashboardURL: result.Urls[0],
            patientFolderURL: result.Urls[1],
            patientDMPManagementURL: result.Urls[2],
            documentsURL: result.Urls[3],
            carePathwayURL: result.Urls[4],
            historyAccessURL: result.Urls[5],
            parametersURL: result.Urls[6],
            rightsAndWillsURL: result.Urls[7]
          });
        }
      });
    });
  }

  networkParams(
    healthCentreConfig?: DmpHealthCentreConfig,
    windowsUser?: string | undefined
  ): NetworkParams {
    const params: NetworkParams = {};

    if (healthCentreConfig) {
      if (healthCentreConfig.proxyAddress) {
        const proxyAddress = healthCentreConfig.proxyAddress.split(":");
        const proxyHost = proxyAddress[0];
        const proxyPort = proxyAddress[1];
        params.s_proxyServer = proxyHost;
        params.i_proxyPort = +proxyPort;
      }

      params.s_proxyLogin = healthCentreConfig.proxyLogin;
      params.s_proxyPass = healthCentreConfig.proxyPassword;

      params.s_ntpServer = healthCentreConfig.ntpAddress;
      if (params.s_ntpServer) {
        params.i_ntpPort = healthCentreConfig.ntpPort || 123;
      }

      if (healthCentreConfig.isTseEnv) {
        if (windowsUser) {
          params.s_tseCredentials = windowsUser;
        } else {
          console.warn(
            "HealthCentre is flagged has being TSE but no Windows user has been given. Please check call to networkParams."
          );
        }
      }
    }

    return params;
  }

  /**
   * Open a new session with DmpConnect server.
   *
   * The Proxy and NTP settings are retrieved from the given HealthCentre.
   */
  openSession(
    healthCentreConfig?: DmpHealthCentreConfig,
    windowsUser?: string | undefined
  ): Promise<void> {
    return new Promise(async (resolve, reject) => {
      // If there is already an opened session, close it
      if (await this.sessionOpened()) {
        await this.closeSession();
      }

      this.cachedNetworkParams = this.networkParams(
        healthCentreConfig,
        windowsUser
      );

      const resultCallback = result => {
        if (result.error) {
          console.error(result.error);
          reject(
            new CnamApiError(
              `Cannot open a new session (hl_openSession)`,
              result.error
            )
          );
        } else {
          resolve(result);
        }
      };

      this.instance.hl_openSessionWithCustomDcParam(
        this._cachedDcParameters,
        null,
        Object.assign(this.cachedNetworkParams, {
          configuration: await this._downloadConfiguration()
        }),
        resultCallback
      );
    });
  }

  async readCPSCard(): Promise<ReadCpxCardResult> {
    if (this.isRemoteMode) {
      return this._readRemoteCPSCard();
    }
    return this._readCPSCard();
  }

  readVitaleCard(): Promise<ReadVitaleCardResult> {
    return new Promise(async (resolve, reject) => {
      const cards = await this.detectCards();
      if (!cards.vitale) {
        reject(new CnamApiVitaleCardError("Vitale card is not detected"));
        return;
      }

      this.instance.hl_getVitaleCard(
        cards.vitale.index,
        cards.vitale.name,
        result => {
          if (result.error) {
            reject(
              new CnamApiVitaleCardError(
                `Cannot get vitale card content : ${result.error.s_apiErrorDescription}`,
                result.error
              )
            );
            return;
          }

          this.instance.hl_readVitaleCard((result: ReadVitaleCardResult) => {
            if (result.error) {
              reject(
                new CnamApiVitaleCardError(
                  `Cannot read vitale card content : ${result.error.s_apiErrorDescription}`,
                  result.error
                )
              );
              return;
            }
            resolve(result);
          });
        }
      );
    });
  }

  /**
   * Load and register the DmpConnect configuration.
   */
  async registerConfiguration(): Promise<void> {
    const configuration = await this._downloadConfiguration();
    await this._setServerConfiguration(configuration);
  }

  selectedHealthCentreForMedic(medic: Medic): string | undefined {
    const selectedHealthCentreForMedicFromLocalStorage =
      getItemFromLocalStorage(this._selectedHealthCentreKey(medic));
    return selectedHealthCentreForMedicFromLocalStorage == null
      ? undefined
      : selectedHealthCentreForMedicFromLocalStorage;
  }

  async sessionOpened(): Promise<boolean> {
    return new Promise(async resolve => {
      this.instance.hl_sessionExists({}, function (result) {
        resolve(result.i_exists == 1);
      });
    });
  }

  /**
   * Set the confidentiality level (default to false)
   * If true, it means that that following transactions will not be recorded in logs
   */
  setConfidentialityLevel(enableSecretConnection: boolean): Promise<unknown> {
    return new Promise((resolve, reject) => {
      this.instance.hl_setConfidentialityLevel(
        enableSecretConnection ? 1 : 0,
        function (result) {
          if (result.error) {
            reject(
              new CnamApiError(
                `Cannot set the confidentiality level (hl_setConfidentialityLevel) : ${result.error.s_apiErrorDescription}`,
                result.error
              )
            );
          } else {
            resolve(result);
          }
        }
      );
    });
  }

  setSelectedHealthCentreForMedic(medic: Medic, value: string): void {
    return setItemInLocalStorage(this._selectedHealthCentreKey(medic), value);
  }

  setWindowsUserForMedic(medic: Medic, value: string | undefined): void {
    const key = this._windowsUserForMedicKey(medic);
    if (!value) {
      removeItemFromLocalStorage(key);
    } else {
      setItemInLocalStorage(key, value);
    }
  }

  updateDmpAccessAuthorization(nir: string): Promise<void> {
    return new Promise(async (resolve, reject) => {
      this.instance.hl_updateUserDmpAccessAuthorization(
        nir,
        DMPConnect.UserAuthorizationAction.AddAuthorization,
        0,
        null,
        function (result) {
          if (result.error) {
            reject(
              new CnamApiError(
                `Cannot authorize current medic (hl_updateUserDmpAccessAuthorization)`,
                result.error
              )
            );
          } else {
            resolve();
          }
        }
      );
    });
  }

  windowsUserForMedic(medic: Medic | undefined): string | undefined {
    return medic
      ? (getItemFromLocalStorage(this._windowsUserForMedicKey(medic)) as string)
      : undefined;
  }

  /**
   * Create a connector to request the DMP.
   */
  protected _createDMPConnector(
    practiceSetting: string,
    cpsPracticeLocationIndice: number,
    practiceLocations: PracticeLocation[]
  ): Promise<unknown> {
    return new Promise((resolve, reject) => {
      const networkParams = this._networkParamsForDmp();
      // Setting a custom PracticeLocation is only available from the 1.6.2 version
      const practiceLocation =
        this.medic?.forceFinessForCnamRetrieval && practiceLocations
          ? {
              s_practiceLocationStructureId:
                this.medic.forceFinessForCnamRetrieval,
              s_practiceLocationName:
                practiceLocations[cpsPracticeLocationIndice].structureName,
              s_practiceLocationActivitySector:
                practiceLocations[cpsPracticeLocationIndice].activity,
              s_practiceLocationPracticeSettings: "AMBULATOIRE"
            }
          : undefined;

      const optionalParameters = {
        ...networkParams,
        PracticeLocation: practiceLocation
      };

      this.instance.hl_createDmpConnector(
        this.pinCodeManager.pinCode as string,
        this.connectionParams.dmpServerName as never,
        this.transactionsTimeoutInSeconds as never,
        practiceSetting as never,
        cpsPracticeLocationIndice as never,
        optionalParameters,
        result => {
          if (result.error) {
            reject(
              new CnamApiError(
                `Cannot create a connector (hl_createDmpConnector) : ${result.error.s_apiErrorDescription}`,
                result.error
              )
            );
          } else {
            resolve(result);
          }
        }
      );
    });
  }

  protected async _createRemoteDMPConnector(
    practiceSetting: string,
    cpsPracticeLocationIndice: number,
    practiceLocations: PracticeLocation[]
  ): Promise<unknown> {
    return new Promise(async (resolve, reject) => {
      const networkParams = this._networkParamsForDmp();
      // Setting a custom PracticeLocation is only available from the 1.6.2 version
      const practiceLocation =
        this.medic?.forceFinessForCnamRetrieval && practiceLocations
          ? {
              s_practiceLocationStructureId:
                this.medic.forceFinessForCnamRetrieval,
              s_practiceLocationName:
                practiceLocations[cpsPracticeLocationIndice].structureName,
              s_practiceLocationActivitySector:
                practiceLocations[cpsPracticeLocationIndice].activity,
              s_practiceLocationPracticeSettings: "AMBULATOIRE"
            }
          : undefined;

      const optionalParameters = {
        ...networkParams,
        PracticeLocation: practiceLocation
      };

      (this.instance as DMPConnectHttpInstance).hl_createDmpConnector(
        this.connectionParams.dmpServerName,
        this.transactionsTimeoutInSeconds,
        practiceSetting,
        cpsPracticeLocationIndice,
        optionalParameters,
        result => {
          if (result.error) {
            reject(
              new CnamApiError(
                `Cannot create a connector (hl_createDmpConnector) : ${result.error.s_apiErrorDescription}`,
                result.error
              )
            );
          } else {
            resolve(result);
          }
        }
      );
    });
  }

  private _configurationFilename(): string {
    if (this.isDevelopmentMode) {
      return "dcparameters.dmpcjs2.dev.manual.dat_semeiajs2.txt";
    }
    return "dcparameters.dmpcjs2.prod.manual.dat_semeiajs2.txt";
  }

  /**
   * Download or read configuration from the cache.
   */
  private async _downloadConfiguration(): Promise<DmpConnectConfiguration> {
    return new Promise(async resolve => {
      if (this._cachedDcParameters) {
        resolve(this._cachedDcParameters);
        return;
      }
      const response = await fetch(
        `/assets/dmpconnectjs/configs/${this._configurationFilename()}`
      );

      this._cachedDcParameters = await response.text();
      resolve(this._cachedDcParameters);
    });
  }

  /**
   * Returns the Network params for the createDmpConnector method.
   * This is based on the DmpConnect params but some attributes have a different name,
   * thus they are renamed.
   */
  private _networkParamsForDmp(): DmpNetworkParams {
    const result: DmpNetworkParams = {};

    const dmpConnectParams = this.cachedNetworkParams;
    for (const key in dmpConnectParams) {
      if (key == "s_proxyServer") {
        result.s_proxyIpOrUrl = dmpConnectParams.s_proxyServer;
      } else if (key == "s_ntpServer") {
        result.s_ntpIpOrURl = dmpConnectParams.s_ntpServer;
      } else if (
        ["i_proxyPort", "s_proxyLogin", "s_proxyPass", "i_ntpPort"].includes(
          key
        )
      ) {
        result[key] = dmpConnectParams[key];
      }
    }

    return result;
  }

  private _selectedHealthCentreKey(medic): string {
    return `service:dmp-connect:medic-${medic.id}:selected-health-centre`;
  }

  private async _setServerConfiguration(
    configuration: DmpConnectConfiguration
  ): Promise<void> {
    const params = {
      s_dcparameters64: configuration
    };

    // Check if registration is already done
    const alreadyRegistered = await new Promise(async (resolve, reject) => {
      this.instance.sendHttpCommand(
        params,
        "isDcParameterRegistered",
        function (e) {
          if (e.error) {
            reject(e.error);
          } else {
            resolve(!!e.i_registered);
          }
        }
      );
    });

    if (!alreadyRegistered) {
      await new Promise<void>(async (resolve, reject) => {
        this.instance.sendHttpCommand(
          params,
          "registerDcParameter",
          function (e) {
            if (e.error) {
              reject(e.error);
            } else {
              resolve();
            }
          }
        );
      });
    }
  }

  private _windowsUserForMedicKey(medic: Medic): string {
    return `service:dmp-connect:medic-${medic.id}:windows-user`;
  }
}
